import { useEffect } from "react";

/**
 * Custom hook to suppress ResizeObserver loop errors
 * These errors are harmless but noisy and occur in React + Ant Design applications
 */
const useResizeObserverErrorSuppression = () => {
  useEffect(() => {
    // Store original functions
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalOnError = window.onerror;
    const originalOnUnhandledRejection = window.onunhandledrejection;

    // Helper function to check if error is ResizeObserver related
    const isResizeObserverError = (message) => {
      return (
        message &&
        typeof message === "string" &&
        (message.includes("ResizeObserver loop") ||
          message.includes("ResizeObserver loop limit exceeded") ||
          message.includes(
            "ResizeObserver loop completed with undelivered notifications"
          ))
      );
    };

    // Override console methods
    console.error = (...args) => {
      if (isResizeObserverError(args[0])) {
        return;
      }
      originalError.apply(console, args);
    };

    console.warn = (...args) => {
      if (isResizeObserverError(args[0])) {
        return;
      }
      originalWarn.apply(console, args);
    };

    // Override global error handler
    window.onerror = (message, source, lineno, colno, error) => {
      if (isResizeObserverError(message)) {
        return true; // Prevent default handling
      }
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }
      return false;
    };

    // Override unhandled promise rejection handler
    window.onunhandledrejection = (event) => {
      if (isResizeObserverError(event.reason?.message)) {
        event.preventDefault();
        return;
      }
      if (originalOnUnhandledRejection) {
        originalOnUnhandledRejection(event);
      }
    };

    // Event handlers for additional coverage
    const handleError = (e) => {
      if (
        isResizeObserverError(e.message) ||
        isResizeObserverError(e.error?.message) ||
        isResizeObserverError(e.reason?.message)
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    window.addEventListener("error", handleError, true);
    window.addEventListener("unhandledrejection", handleError, true);

    // Cleanup function
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.onerror = originalOnError;
      window.onunhandledrejection = originalOnUnhandledRejection;
      window.removeEventListener("error", handleError, true);
      window.removeEventListener("unhandledrejection", handleError, true);
    };
  }, []);
};

export default useResizeObserverErrorSuppression;
